import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, Download } from "lucide-react";

export default function AdminSubscribers() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http
      .get("/admin/subscribers")
      .then(({ data }) => setSubs(data.subscribers || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const exportCsv = () => {
    if (!subs.length) return;
    const header = "email,subscribed_at\n";
    const rows = subs.map((s) => `${s.email},${s.created_at}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `the-tea-list-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  let body;
  if (loading) {
    body = (
      <div className="p-8 text-center text-[#66726d] font-mono text-xs uppercase tracking-widest animate-pulse">
        loading…
      </div>
    );
  } else if (subs.length === 0) {
    body = <div className="p-8 text-center text-[#66726d]">No subscribers yet.</div>;
  } else {
    body = subs.map((s) => (
      <div
        key={s.id}
        className="grid grid-cols-12 gap-4 items-center px-5 py-4 border-t"
        style={{ borderColor: "var(--border)" }}
        data-testid={`subscriber-row-${s.email}`}
      >
        <div className="col-span-8 font-serif text-base text-[#151e1a] break-all">{s.email}</div>
        <div className="col-span-4 font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
          {new Date(s.created_at).toLocaleString()}
        </div>
      </div>
    ));
  }

  return (
    <div
      className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20"
      data-testid="admin-subscribers-page"
    >
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#66726d] hover:text-[#1e4a35] mb-6"
        data-testid="back-to-admin"
      >
        <ChevronLeft size={14} /> Back to dashboard
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <p className="overline mb-3">Admin · Newsletter</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
            Subscribers
          </h1>
          <p className="mt-4 text-[#404e48] max-w-lg font-light">
            {loading ? "…" : `${subs.length} ${subs.length === 1 ? "person has" : "people have"} signed up to the newsletter.`}
          </p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={!subs.length}
          className="btn-primary inline-flex items-center gap-2 self-start disabled:opacity-50"
          data-testid="export-subscribers-csv"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="border" style={{ borderColor: "var(--border)" }}>
        <div className="grid grid-cols-12 gap-4 px-5 py-4 bg-[#f1eadb] font-mono text-[10px] uppercase tracking-widest text-[#404e48]">
          <div className="col-span-8">Email</div>
          <div className="col-span-4">Subscribed</div>
        </div>
        {body}
      </div>
    </div>
  );
}
